import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, useMediaQuery, useTheme } from '@mui/material';
import { styled, Theme, CSSObject } from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import LogoutIcon from '@mui/icons-material/Logout';
import { LinierCloseIcon, NotificationsIcon } from '../../components/Icons';
import Typography from '../Typography';
import { WhiteMarcoLogo, MiniWhiteMarcoLogo } from '../MarcoLogo';
import Button from '../Button';
import { useLazyTranslation } from '../../hooks/i18n';
import { Nullable, User } from '../../types';
import { APINotification } from '../../types/companyUser';
import {
  MY_ACCOUNT_ADMIN,
  MY_ACCOUNT,
  LENDER_PROFILE,
  ADMIN_TAKEOVER_TOKEN_STORAGE_KEY,
} from '../../constants/auth';
import { COLORS } from '../../themev2';
import AppMenu from './AppMenu';

export const drawerWidth = 220;
export const closedDrawerWidth = 68;

const getProfileRoute = ({ isLender, isAdmin }: { isLender: boolean; isAdmin: boolean }) => {
  if (isLender) return LENDER_PROFILE;
  if (isAdmin) return MY_ACCOUNT_ADMIN;
  return MY_ACCOUNT;
};

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  width: closedDrawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
});

const DefaultDrawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  })
);

const MobileDrawer = styled(MuiDrawer)({
  // This is to make sure the drawer is on top of the chatbot script
  zIndex: 999999999,
  '& .MuiDrawer-paper': {
    width: '100%',
    boxSizing: 'border-box',
  },
});

type DrawerProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  isAdmin: boolean;
  isLender?: boolean;
  user: Nullable<User>;
  logoutUser: () => void;
  alerts?: APINotification[];
};

const Drawer = ({ open, setOpen, isAdmin, isLender = false, user, logoutUser, alerts = [] }: DrawerProps) => {
  const theme = useTheme();
  const { t } = useLazyTranslation();
  const navigate = useNavigate();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const [isUserMenuOpen, setIsUserMenuOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
    setIsUserMenuOpen(false);
  };

  const handleDrawerClick = (event: React.MouseEvent) => {
    // If clicking outside the menu but inside drawer, close the menu
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      setIsUserMenuOpen(false);
    }
  };

  const handleOnClickItemCallback = () => {
    if (!isMdUp) {
      setOpen(false);
      setIsUserMenuOpen(false);
    }
  };

  const handleLogout = () => {
    logoutUser();
    handleDrawerClose();
  };

  const DrawerContent = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
        padding: '28px 16px 16px 16px',
      }}
      onMouseEnter={handleDrawerOpen}
      onMouseLeave={handleDrawerClose}
      onClick={handleDrawerClick}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: open && !isMdUp ? 'space-between' : open && isMdUp ? 'flex-start' : 'center',
            alignItems: 'center',
          }}
        >
          {open ? <WhiteMarcoLogo /> : <MiniWhiteMarcoLogo />}
          {open && !isMdUp && (
            <IconButton onClick={handleDrawerClose} sx={{ color: COLORS.gray[1000] }}>
              <LinierCloseIcon />
            </IconButton>
          )}
        </Box>
        <Box>
          <AppMenu
            isAdmin={isAdmin}
            isLender={isLender}
            handleOnClickItemCallback={handleOnClickItemCallback}
            isDrawerOpened={open}
            openDrawer={handleDrawerOpen}
          />
        </Box>
      </Box>
      <Box>
        {!!localStorage.getItem(ADMIN_TAKEOVER_TOKEN_STORAGE_KEY) && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '12px',
            }}
          >
            {open ? (
              <Button
                startIcon={<LogoutIcon sx={{ width: 20, height: 20 }} />}
                onClick={() => logoutUser()}
                fullWidth
              >
                {t('backToAdminAppBtn')}
              </Button>
            ) : (
              <IconButton onClick={() => logoutUser()} sx={{ color: COLORS.brand.paper }}>
                <LogoutIcon sx={{ width: 20, height: 20 }} />
              </IconButton>
            )}
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            justifyContent: open ? 'flex-start' : 'center',
          }}
        >
          <Box>
            <Badge
              color="primary"
              variant="dot"
              overlap="circular"
              invisible={alerts.length === 0}
              sx={{
                '& .MuiBadge-badge': {
                  backgroundColor: '#D9AA32',
                  transform: 'translate(0px, 0px)',
                },
              }}
            >
              <NotificationsIcon />
            </Badge>
          </Box>
          {open && (
            <Typography
              sx={{ fontSize: '14px', fontWeight: '400', lineHeight: '21px', color: COLORS.brand.paper }}
            >
              {t('notifications')}
            </Typography>
          )}
        </Box>
        <Divider sx={{ borderColor: '#FFFFFF0D', marginY: '12px' }} />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            cursor: 'pointer',
            borderRadius: 1,
            justifyContent: open ? 'space-between' : 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                backgroundColor: COLORS.gray[1100],
                width: 24,
                height: 24,
              }}
            >
              <Typography
                sx={{ fontSize: '14px', fontWeight: '400', lineHeight: '21px', color: COLORS.brand.paper }}
              >
                {user?.firstName.charAt(0).toUpperCase()}
              </Typography>
            </Avatar>
            {open && (
              <Typography
                sx={{ color: COLORS.brand.paper, fontSize: '14px', fontWeight: '400', lineHeight: '21px' }}
              >
                {user?.fullName}
              </Typography>
            )}
          </Box>
          {open && (
            <div ref={menuRef}>
              {isUserMenuOpen && (
                <Box
                  sx={{
                    zIndex: 1000,
                    position: 'absolute',
                    bottom: '72px',
                    right: '12px',
                    width: '144px',
                    backgroundColor: COLORS.brand.paper,
                    borderRadius: '8px',
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                  }}
                  onClick={(e) => e.stopPropagation()} // Prevent clicks inside menu from closing it
                >
                  <List>
                    <ListItem
                      sx={{
                        '&:hover': {
                          backgroundColor: COLORS.gray[300],
                        },
                      }}
                      onClick={() => {
                        navigate(getProfileRoute({ isLender, isAdmin }));
                        if (!isMdUp) {
                          handleDrawerClose();
                        }
                      }}
                    >
                      <Typography
                        textAlign="start"
                        sx={{ fontSize: '14px', fontWeight: '400', lineHeight: '21px' }}
                      >
                        {t('myAccount')}
                      </Typography>
                    </ListItem>
                    <Box sx={{ height: '1px', backgroundColor: COLORS.gray[500], margin: '0 8px' }} />
                    <ListItem
                      onClick={handleLogout}
                      sx={{
                        '&:hover': {
                          backgroundColor: COLORS.gray[300],
                        },
                      }}
                    >
                      <Typography
                        textAlign="start"
                        sx={{ color: '#930909', fontSize: '14px', fontWeight: '700', lineHeight: '21px' }}
                      >
                        {t('logout')}
                      </Typography>
                    </ListItem>
                  </List>
                </Box>
              )}
              <IconButton
                onClick={(e) => {
                  e.stopPropagation(); // Prevent drawer click handler from firing
                  setIsUserMenuOpen(!isUserMenuOpen);
                }}
                sx={{
                  backgroundColor: isUserMenuOpen ? '#FFFFFF1A' : 'transparent',
                  '&:hover': {
                    backgroundColor: '#FFFFFF1A',
                  },
                  borderRadius: '4px',
                }}
              >
                <MoreHoriz sx={{ color: COLORS.gray[900] }} />
              </IconButton>
            </div>
          )}
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      {isMdUp ? (
        <DefaultDrawer
          variant="permanent"
          open={open}
          PaperProps={{
            sx: {
              backgroundColor: isAdmin ? '#065570' : COLORS.gray[1400],
              display: 'flex',
              flexDirection: 'column',
              height: '100vh',
            },
          }}
        >
          <DrawerContent />
        </DefaultDrawer>
      ) : (
        <MobileDrawer
          anchor="left"
          variant="temporary"
          open={open}
          onClose={handleDrawerClose}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: {
              backgroundColor: isAdmin ? '#065570' : COLORS.gray[1400],
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          <DrawerContent />
        </MobileDrawer>
      )}
    </>
  );
};

export default Drawer;
